import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { adminApi, publicApi, session, type Component } from "../../lib/api";

const SEVERITIES = ["minor", "major", "critical"];

export function OpenIncident() {
  const navigate = useNavigate();
  const [components, setComponents] = useState<Component[]>([]);
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState("major");
  const [body, setBody] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session.get()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    publicApi
      .status()
      .then((s) => setComponents([...s.components.core, ...s.components.upstream]))
      .catch((e) => setError(e instanceof Error ? e.message : "unknown"));
  }, [navigate]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.openIncident({
        title: title.trim(),
        body_markdown: body,
        severity,
        status: "investigating",
        affected_components: [...selected],
      });
      navigate("/admin/dashboard", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "unknown");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="text-2xl font-semibold">Open a new incident</h1>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium">Title</span>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Severity</span>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend className="text-sm font-medium">Affected components</legend>
          <ul className="mt-2 grid grid-cols-2 gap-2">
            {components.map((c) => (
              <li key={c.component_id}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(c.component_id)}
                    onChange={() => toggle(c.component_id)}
                  />
                  {c.name}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        <label className="block">
          <span className="text-sm font-medium">Update body (markdown)</span>
          <textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
          />
        </label>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded border border-slate-300 px-3 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-slate-900 px-4 py-2 text-white disabled:bg-slate-400"
          >
            {submitting ? "Posting\u2026" : "Open incident"}
          </button>
        </div>
      </form>
    </section>
  );
}
